import { useState, useEffect } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  ClockCounterClockwise,
  TrendUp,
  TrendDown,
  ChartLineUp,
  Check,
  X,
  ArrowsClockwise,
  Wallet,
  DownloadSimple
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const StatsCard = ({ icon: Icon, label, value, trend, iconBg }) => (
  <Card className="border-slate-200 rounded-sm shadow-none" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-sm ${iconBg || 'bg-slate-100'}`}>
          <Icon size={18} className="text-slate-600" weight="regular" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-body">{label}</p>
          <p className={`font-data text-xl font-medium tabular-nums ${
            trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-slate-900'
          }`}>
            {value}
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const formatPrice = (price) => {
  if (price == null) return '—';
  if (price >= 1000) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 1) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const StatusBadge = ({ trade }) => {
  if (trade.status === 'open') {
    return <Badge data-testid={`status-badge-${trade.id}`} className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-0 font-body">Ouvert</Badge>;
  }
  const pnl = trade.profit_loss || 0;
  if (pnl > 0) {
    return <Badge data-testid={`status-badge-${trade.id}`} className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0 font-body">Gagnant</Badge>;
  }
  if (pnl < 0) {
    return <Badge data-testid={`status-badge-${trade.id}`} className="bg-red-100 text-red-700 hover:bg-red-100 border-0 font-body">Perdant</Badge>;
  }
  return <Badge data-testid={`status-badge-${trade.id}`} className="bg-slate-100 text-slate-600 hover:bg-slate-100 border-0 font-body">Neutre</Badge>;
};

const PnLCell = ({ trade }) => {
  const pnl = trade.profit_loss;
  const pnlPct = trade.profit_loss_percent;
  
  if (pnl == null && trade.status === 'open') {
    return <span className="text-slate-400 text-sm font-body">Chargement...</span>;
  }
  if (pnl == null) return <span className="text-slate-400">—</span>;

  const color = pnl > 0 ? 'text-emerald-600' : pnl < 0 ? 'text-red-600' : 'text-slate-500';
  const sign = pnl >= 0 ? '+' : '';

  return (
    <div className="flex flex-col" data-testid={`pnl-cell-${trade.id}`}>
      <span className={`font-data tabular-nums font-medium ${color}`}>
        {sign}${pnl.toFixed(2)}
      </span>
      {pnlPct != null && (
        <span className={`font-data tabular-nums text-xs ${color}`}>
          {sign}{pnlPct.toFixed(2)}%
        </span>
      )}
    </div>
  );
};

const TradeRow = ({ trade, onClose }) => (
  <TableRow data-testid={`trade-row-${trade.id}`} className="hover:bg-slate-50/50">
    <TableCell>
      <div className="flex items-center gap-2">
        <span className="font-heading font-bold text-slate-900">{trade.symbol}</span>
        <Badge variant="outline" className="text-[10px] font-data px-1.5 py-0">{trade.market_type}</Badge>
      </div>
    </TableCell>
    <TableCell className="font-data tabular-nums text-slate-700">
      {formatPrice(trade.entry_price)}
    </TableCell>
    <TableCell className="font-data tabular-nums text-slate-700">
      {trade.status === 'open' && trade.current_price
        ? <span className="text-blue-600">{formatPrice(trade.current_price)}</span>
        : <span className="text-slate-400">—</span>
      }
    </TableCell>
    <TableCell className="font-data tabular-nums text-slate-700">
      {trade.exit_price ? formatPrice(trade.exit_price) : <span className="text-slate-400">—</span>}
    </TableCell>
    <TableCell className="font-data tabular-nums text-slate-700">
      ${trade.position_size?.toLocaleString()}
    </TableCell>
    <TableCell>
      <PnLCell trade={trade} />
    </TableCell>
    <TableCell>
      <StatusBadge trade={trade} />
    </TableCell>
    <TableCell className="font-data tabular-nums text-xs text-slate-500">
      {formatDate(trade.created_at)}
    </TableCell>
    <TableCell>
      {trade.status === 'open' && (
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => onClose(trade)}
          className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
          data-testid={`close-trade-${trade.id}`}
        >
          <X size={14} className="mr-1" />
          <span className="text-xs">Clôturer</span>
        </Button>
      )}
    </TableCell>
  </TableRow>
);

export default function TradeHistory() {
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [exitPrice, setExitPrice] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const [tradesResponse, statsResponse] = await Promise.all([
        fetch(`${API}/trades${statusParam}`),
        fetch(`${API}/stats`)
      ]);
      const [tradesData, statsData] = await Promise.all([
        tradesResponse.json(),
        statsResponse.json()
      ]);
      setTrades(tradesData);
      setStats(statsData);
    } catch (error) {
      console.error("Error fetching trade data:", error);
      toast.error("Erreur lors du chargement des trades");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleOpenCloseDialog = (trade) => {
    setSelectedTrade(trade);
    setExitPrice("");
    setCloseDialogOpen(true);
  };

  const handleCloseTrade = async () => {
    if (!exitPrice || parseFloat(exitPrice) <= 0) {
      toast.error("Veuillez entrer un prix de sortie valide");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/trades/${selectedTrade.id}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ exit_price: parseFloat(exitPrice) })
      });
      if (!response.ok) throw new Error("Failed to close trade");
      toast.success("Trade cloture avec succes!");
      setCloseDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error closing trade:", error);
      toast.error("Erreur lors de la cloture du trade");
    } finally {
      setSubmitting(false);
    }
  };

  const openTrades = trades.filter(t => t.status === 'open');

  if (loading) {
    return (
      <div className="space-y-4" data-testid="history-loading">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div data-testid="history-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Historique des Trades</h1>
          <p className="text-sm text-slate-500 font-body mt-1">
            {openTrades.length} position{openTrades.length !== 1 ? 's' : ''} ouverte{openTrades.length !== 1 ? 's' : ''} &middot; {trades.length} trade{trades.length !== 1 ? 's' : ''} au total
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="rounded-sm"
            data-testid="refresh-trades-btn"
          >
            <ArrowsClockwise size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Actualisation...' : 'Actualiser'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              window.open(`${API}/trades/export`, '_blank');
              toast.success("Export CSV en cours...");
            }}
            className="rounded-sm"
            data-testid="export-trades-btn"
          >
            <DownloadSimple size={14} className="mr-1.5" />
            Export CSV
          </Button>
          <div className="flex gap-1 ml-2">
            {[
              { key: 'all', label: 'Tous' },
              { key: 'open', label: `Ouverts (${openTrades.length})` },
              { key: 'closed', label: 'Clotures' }
            ].map(f => (
              <Button
                key={f.key}
                variant={filter === f.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f.key)}
                className="rounded-sm text-xs"
                data-testid={`filter-${f.key}-trades`}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="stats-cards">
        <StatsCard
          icon={ChartLineUp}
          label="Total Trades"
          value={stats?.total_trades || 0}
          iconBg="bg-slate-100"
        />
        <StatsCard
          icon={TrendUp}
          label="Win Rate"
          value={stats?.win_rate ? `${stats.win_rate}%` : '0%'}
          trend={stats?.win_rate >= 50 ? 'up' : stats?.win_rate > 0 ? 'down' : undefined}
          iconBg="bg-emerald-50"
        />
        <StatsCard
          icon={stats?.total_profit_loss >= 0 ? TrendUp : TrendDown}
          label="P&L Total"
          value={stats?.total_profit_loss != null ? `${stats.total_profit_loss >= 0 ? '+' : ''}$${stats.total_profit_loss.toFixed(2)}` : '$0.00'}
          trend={stats?.total_profit_loss > 0 ? 'up' : stats?.total_profit_loss < 0 ? 'down' : undefined}
          iconBg={stats?.total_profit_loss >= 0 ? 'bg-emerald-50' : 'bg-red-50'}
        />
        <StatsCard
          icon={Wallet}
          label="P&L Non Realise"
          value={stats?.unrealized_pnl != null ? `${stats.unrealized_pnl >= 0 ? '+' : ''}$${stats.unrealized_pnl.toFixed(2)}` : '$0.00'}
          trend={stats?.unrealized_pnl > 0 ? 'up' : stats?.unrealized_pnl < 0 ? 'down' : undefined}
          iconBg="bg-blue-50"
        />
      </div>

      {/* Trades Table */}
      <Card className="border-slate-200 rounded-sm shadow-none" data-testid="trades-table-card">
        <CardContent className="p-0">
          {trades.length === 0 ? (
            <div className="text-center py-12">
              <ClockCounterClockwise size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 font-body">Aucun trade enregistre</p>
              <p className="text-sm text-slate-400 font-body mt-1">
                Vos trades apparaitront ici une fois que vous aurez entre des positions
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="font-body text-[10px] uppercase tracking-wider text-slate-500">Symbole</TableHead>
                    <TableHead className="font-body text-[10px] uppercase tracking-wider text-slate-500">Entree</TableHead>
                    <TableHead className="font-body text-[10px] uppercase tracking-wider text-slate-500">Prix Actuel</TableHead>
                    <TableHead className="font-body text-[10px] uppercase tracking-wider text-slate-500">Sortie</TableHead>
                    <TableHead className="font-body text-[10px] uppercase tracking-wider text-slate-500">Taille</TableHead>
                    <TableHead className="font-body text-[10px] uppercase tracking-wider text-slate-500">P&L</TableHead>
                    <TableHead className="font-body text-[10px] uppercase tracking-wider text-slate-500">Statut</TableHead>
                    <TableHead className="font-body text-[10px] uppercase tracking-wider text-slate-500">Date</TableHead>
                    <TableHead className="font-body text-[10px] uppercase tracking-wider text-slate-500">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trades.map((trade) => (
                    <TradeRow 
                      key={trade.id}
                      trade={trade}
                      onClose={handleOpenCloseDialog}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Close Trade Dialog */}
      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Cloturer le Trade</DialogTitle>
            <DialogDescription className="font-body">
              Entrez le prix de sortie pour {selectedTrade?.symbol}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500 font-body">Prix d'entree</p>
                <p className="font-data font-medium">{formatPrice(selectedTrade?.entry_price)}</p>
              </div>
              <div>
                <p className="text-slate-500 font-body">Taille</p>
                <p className="font-data font-medium">${selectedTrade?.position_size?.toLocaleString()}</p>
              </div>
              {selectedTrade?.current_price && (
                <div>
                  <p className="text-slate-500 font-body">Prix actuel</p>
                  <p className="font-data font-medium text-blue-600">{formatPrice(selectedTrade?.current_price)}</p>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="exit-price" className="font-body">Prix de Sortie (USD)</Label>
              <Input 
                id="exit-price"
                type="number"
                step="0.0001"
                placeholder={selectedTrade?.current_price ? selectedTrade.current_price.toString() : "0.00"}
                value={exitPrice}
                onChange={(e) => setExitPrice(e.target.value)}
                className="font-data rounded-sm"
                data-testid="exit-price-input"
              />
            </div>
            
            {exitPrice && selectedTrade && (
              <div className="p-3 bg-slate-50 rounded-sm">
                <p className="text-sm text-slate-500 mb-1 font-body">P&L Estime</p>
                <p className={`font-data text-lg font-medium ${
                  (parseFloat(exitPrice) - selectedTrade.entry_price) >= 0 ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {((parseFloat(exitPrice) - selectedTrade.entry_price) * selectedTrade.position_size) >= 0 ? '+' : ''}
                  ${((parseFloat(exitPrice) - selectedTrade.entry_price) * selectedTrade.position_size).toFixed(2)}
                  <span className="text-xs ml-2">
                    ({(((parseFloat(exitPrice) - selectedTrade.entry_price) / selectedTrade.entry_price) * 100).toFixed(2)}%)
                  </span>
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setCloseDialogOpen(false)}
              className="rounded-sm"
              data-testid="cancel-close-btn"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleCloseTrade}
              disabled={submitting}
              className="rounded-sm bg-red-600 hover:bg-red-700"
              data-testid="confirm-close-btn"
            >
              {submitting ? "Cloture..." : "Cloturer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
